import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import {
    collection, doc, getDoc, getDocs, addDoc, updateDoc,
    query, where, serverTimestamp
} from 'firebase/firestore';
import { getAppSubscription } from '../config/pricing';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFeatures, paywallActive, isPgReviewer, isBillingTester, getBillingOpenAtMs, DEFAULT_BILLING_OPEN_AT_MS } from '../utils/features';
import { isLiveSub, getGraceInfo, resolveBillingOwner, LAB_PLANS, planForCode, DEFAULT_LAB_PLAN } from '../utils/billingPolicy';
import TermsOfService from './TermsOfService';
import JigUsagePlan from './JigUsagePlan';
import JigUsageBar, { useJigUsage } from './common/JigUsageBar';   // 🧩 구독 플랜제(2026-10-30) — 지그 한도 대비 % 막대
import { COMPANY_NAME, CEO_NAME, BIZ_ADDRESS, BIZ_NUMBER, ECOM_REPORT_NO, CONTACT_PHONE } from './common/SiteFooter';

// 결제 동의 문안 판 (법무검토 ④ 결제 화면 필수 문구). 서버 견적의 consentTextVersion 이 우선, 없을 때만 이 값.
// 서버 계약: recordSubscriptionConsent({ agreed:true, expectedAmount, consentTextVersion }) — 문안을 바꾸면 서버와 함께 판을 올린다.
const CONSENT_TEXT_VERSION = 'pay-1.0';

// 견적이 '대상 아님'이 아니라 '요금 정보를 확인 못 함'인 사유 (서버 reasonToError → billing_unavailable 과 같은 묶음)
const QUOTE_UNAVAILABLE_REASONS = ['features_read_failed', 'tariff_read_failed', 'tariff_unknown'];

// 서버 구독 결제 오류 코드(HttpsError details.code) → 화면 문구
const BILLING_ERROR_TEXT = {
    consent_required: '결제 동의 기록이 없습니다. 금액과 자동결제 조건을 확인하고 동의에 체크한 뒤 다시 시도해 주세요.',
    consent_expired: '결제 동의가 만료되었습니다. 금액과 자동결제 조건을 다시 확인하고 동의에 체크해 주세요.',
    consent_amount_mismatch: '동의하신 금액과 청구 금액이 다릅니다. 화면의 금액을 다시 확인하고 동의해 주세요.',
    quote_changed: '요금 정보가 바뀌었습니다. 바뀐 금액과 조건을 확인한 뒤 다시 동의해 주세요.',
    not_subject: '이 계정은 구독 결제 대상이 아닙니다. 결제나 카드 등록이 필요 없습니다.',
    staff_account: '구독 결제는 업체 대표 계정에서만 할 수 있습니다. 업체 대표에게 문의하세요.',
    charge_in_progress: '결제가 이미 진행 중입니다. 잠시 후 화면을 새로고침해 결과를 확인해 주세요.',
    no_payment_method: '등록된 결제 카드가 없습니다. 카드를 먼저 등록해 주세요.',
    billing_unavailable: '요금 정보를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.',
    // 서버 계약 변경 반영 (2026-10-04 dev-server: 결제 사고 복구·결제사 스위치)
    pg_not_active: '결제 준비 중입니다. 잠시 후 다시 시도해 주세요.',
    payment_record_failed: '결제는 완료됐을 수 있습니다. 다시 결제하지 마시고 [결제 확인]을 눌러 주세요. 계속되면 010-2972-3733으로 연락해 주세요.',
    payment_result_unknown: '결제 결과를 확인하고 있습니다. 다시 결제하지 마시고 [결제 확인]을 눌러 주세요.',
    payment_record_pending: '확인 중인 이전 결제가 있습니다. 다시 결제하지 마시고 [결제 확인]을 눌러 주세요. 계속되면 010-2972-3733으로 연락해 주세요.',
    // 본인 환불 계정당 1회 (대표 결정 2026-10-04) — 서버 kispg/tossCancelPayment 가 초과 시 반환
    self_refund_limit: '화면에서 하는 직접 환불은 1회까지입니다. 환불·청약철회는 고객센터(010-2972-3733)에서 계속 받습니다.',
};
// 결제가 됐을 수도 있는 오류 — 구독·결제내역을 다시 읽고, 재결제 버튼을 잠시 막는다(이중 결제 방지)
const PAYMENT_CHECK_CODES = ['payment_record_failed', 'payment_result_unknown', 'payment_record_pending'];
const PAY_RETRY_LOCK_MS = 60 * 1000;
// "실패했습니다" 접두를 붙이면 안 되는 오류 — 실패가 아니라 확인 중·준비 중인 상태
const NOT_FAILURE_CODES = [...PAYMENT_CHECK_CODES, 'pg_not_active', 'charge_in_progress'];
// 동의를 다시 받아야 하는 오류 — 체크 해제 + 견적 다시 읽기
const RECONSENT_CODES = ['consent_required', 'consent_expired', 'consent_amount_mismatch', 'quote_changed'];
const billingErrorCode = (e) => e?.details?.code || null;

// 서버 견적의 날짜 값을 Date로 (형식 미확정 — 'YYYY-MM-DD' 문자열·ms 숫자·Timestamp·{_seconds} 모두 허용)
// TODO(dev-server 계약): getSubscriptionQuote의 trialEndsAt·firstChargeDate 직렬화 형식 확정 후 단순화
const quoteDate = (v) => {
    if (!v) return null;
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
        const [y, m, d] = v.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    if (v.toDate) return v.toDate();
    if (typeof v._seconds === 'number') return new Date(v._seconds * 1000);
    if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
};

// 토스페이먼츠 v2 SDK 동적 로드 (pgProvider='toss'일 때만 사용)
const loadTossSdk = () => new Promise((resolve, reject) => {
    if (window.TossPayments) { resolve(window.TossPayments); return; }
    const s = document.createElement('script');
    s.src = 'https://js.tosspayments.com/v2/standard';
    s.onload = () => resolve(window.TossPayments);
    s.onerror = () => reject(new Error('토스 결제 모듈을 불러오지 못했습니다'));
    document.head.appendChild(s);
});

function AppSubscription({ user }) {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // 회원 유형에 따른 구독 요금·기능 조회 (치과 20만 / 기공소 10만)
    const businessType = user?.businessType || user?.companyBusinessType;
    const pricing = getAppSubscription(businessType);
    const isClinic = businessType === 'clinic' || businessType === 'dental' || businessType === '치과';

    const [billingKey, setBillingKey] = useState(null);
    // eslint-disable-next-line no-unused-vars -- 보존(미사용) 코드, 요금 변경 작업 범위 밖
    const [trialRequest, setTrialRequest] = useState(null);
    const [requestingTrial, setRequestingTrial] = useState(false);
    // PC: 카드 등록을 iframe 모달로 (모바일은 전체 페이지 이동)
    const [showCardModal, setShowCardModal] = useState(false);
    // 결제사 스위치 (config/features.pgProvider) — 기본 kispg, 'toss'는 토스 결제창 경로 (2026-08)
    const [pgProvider, setPgProvider] = useState('kispg');
    // 전면 무료 스위치 (대표 확정 2026-08-24) — ON이면 구독·결제 화면 대신 무료 안내만
    const [allFreeOn, setAllFreeOn] = useState(false);
    // 카드 등록 개시 시각 (config/features.billingOpenAt, 기본 2026-10-11 00:00 KST — 처리방침 v3.2.3 시행 전 토스 전달 금지)
    //   초기값·설정 읽기 실패 시 모두 기본값 → 잠금 유지(fail-closed)
    const [billingOpenAtMs, setBillingOpenAtMs] = useState(DEFAULT_BILLING_OPEN_AT_MS);
    const cardRegOpen = Date.now() >= billingOpenAtMs;
    const billingOpenDate = new Date(billingOpenAtMs);
    const cardRegClosedText = `카드 등록은 ${billingOpenDate.getMonth() + 1}월 ${billingOpenDate.getDate()}일부터 할 수 있습니다.`;
    // 서버 견적 (getSubscriptionQuote, 계획 S9) — 화면 금액·첫 결제일은 이 값만 쓴다(화면·서버 불일치 = 과오청구 차단)
    const [quote, setQuote] = useState(null);
    const [quoteError, setQuoteError] = useState('');
    // 결제 동의 체크 (법무검토 ④ 필수 체크박스) — 체크 전에는 카드 등록·구독 시작 버튼 비활성
    const [consentChecked, setConsentChecked] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    // 결제 재시도 잠금 시각 (결과 확인 중 오류 직후 재결제 버튼 비활성 — 이중 결제 방지)
    const [payLockUntil, setPayLockUntil] = useState(0);
    const payLocked = payLockUntil > Date.now();
    // 결제 확인이 필요한 오류(PAYMENT_CHECK_CODES)를 받은 상태 — [결제 확인] 버튼 노출 (recoverMyPayment)
    const [payCheckNeeded, setPayCheckNeeded] = useState(false);
    const [payChecking, setPayChecking] = useState(false);
    // 본인 요금표가 있는 계정은 companyId 가 있어도 직원이 아니라 본인 기준으로 결제·해지한다
    //   (보안 P1-1, 서버 resolveOwner·buildQuoteForCaller 와 같은 규칙 — billingPolicy.resolveBillingOwner 한 곳)
    const [ownTariffWithCompany, setOwnTariffWithCompany] = useState(false);
    const isStaff = !!user?.companyId && !ownTariffWithCompany;
    // 🧩 구독 플랜제 (2026-10-30, 계획_구독플랜제 §1·§2) — config/features(planV8At 판정용)·플랜 변경 진행 상태·지그 사용량
    const [featuresDoc, setFeaturesDoc] = useState(null);
    const [changingPlan, setChangingPlan] = useState('');   // 변경 중인 플랜 코드
    const isLab = !isClinic && (businessType === 'lab' || (!businessType && user?.userType === 'lab'));
    const jigUsage = useJigUsage(isLab && !!user?.uid, [user?.uid]);
    useEffect(() => {
        let alive = true;
        setOwnTariffWithCompany(false);
        if (user?.uid && user?.companyId && user.companyId !== user.uid) {
            resolveBillingOwner(user).then(r => {
                if (alive && r.selfTariff) setOwnTariffWithCompany(true);
            }).catch(e => console.error('[구독] 판정 대상 확인 실패 — 직원 기준 유지:', e?.message || e));
        }
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid, user?.companyId]);
    // 본인 요금표 확인이 끝나 본인 기준으로 바뀌면 견적을 다시 읽는다(첫 조회는 직원으로 보고 건너뜀)
    useEffect(() => {
        if (ownTariffWithCompany) loadQuote();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownTariffWithCompany]);
    // 결제사 설정 읽기 — pg_not_active(화면·서버 결제사 불일치) 시 다시 읽는다
    const loadPgSetting = () => {
        // 결제사 심사용 계정(pgReviewUserIds)은 전면 무료 중에도 원래 구독 화면 + 토스 결제창 (2026-09-21 토스 가맹 심사)
        return getFeatures().then(f => {
            setFeaturesDoc(f);   // 플랜제: planV8At 판정(옛 33,000원 → 베이직 표시)에 쓴다
            const reviewer = isPgReviewer(f, user?.uid);
            setPgProvider((f.pgProvider === 'toss' || reviewer) ? 'toss' : 'kispg');
            // 시험 결제 계정(billingTestUserIds, 대표 10-15)도 전면 무료 중 원래 구독 화면 — 결제사는 설정값(pgProvider) 그대로
            //   W8(계획_가입승인결제흐름): 무료 안내 판정 = paywallActive 한 곳 (= allFree !== true). 안내형 분기(아래 allFreeOn)도 이 기준을 따른다
            setAllFreeOn(!paywallActive(f) && !reviewer && !isBillingTester(f, user?.uid));
            setBillingOpenAtMs(getBillingOpenAtMs(f));
        }).catch((e) => {
            // 조용한 실패 금지 — 기본값(kispg·무료 스위치 꺼짐)으로 두고 기록. 결제사가 다르면 서버가 pg_not_active 로 거부한다
            console.error('[구독] 결제 설정(config/features) 읽기 실패 — 기본값 유지:', e?.message || e);
        });
    };
    useEffect(() => {
        loadPgSetting();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid]);

    // iframe 모달에서 KISPG/토스 복귀 페이지가 보내는 postMessage 수신
    useEffect(() => {
        const onMsg = (e) => {
            const d = e.data;
            if (!d || (d.type !== 'kispgBilling' && d.type !== 'tossBilling')) return;
            setShowCardModal(false);
            if (d.status === 'success') {
                showMessage('success', '카드 등록이 완료되었습니다. 자동결제를 연결합니다...');
                setTimeout(() => { autoChargeAfterCardRegister(d.type === 'tossBilling' ? 'toss' : 'kispg'); }, 1500);
            } else {
                showMessage('error', `카드 등록 실패: ${d.msg || '다시 시도해주세요'}`);
                setProcessing(false);
            }
        };
        window.addEventListener('message', onMsg);
        return () => window.removeEventListener('message', onMsg);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (user?.uid) {
            loadSubscriptionData();
            loadBillingKey();
            loadTrialRequest();
            loadQuote();

            // URL 파라미터로 빌키 발급 결과 확인
            const params = new URLSearchParams(window.location.search);
            const billingResult = params.get('billing');
            const billingPg = params.get('pg') === 'toss' ? 'toss' : 'kispg'; // 토스 복귀는 pg=toss로 구분
            if (billingResult === 'success') {
                // 빌키 발급 성공 → 자동으로 첫 정기결제 청구
                showMessage('success', '카드 등록이 완료되었습니다. 첫 결제를 진행합니다...');
                window.history.replaceState({}, '', '/subscription');
                // 약간의 딜레이 후 자동 청구 (Firestore 빌키 저장 완료 대기)
                setTimeout(() => {
                    autoChargeAfterCardRegister(billingPg);
                }, 1500);
            } else if (billingResult === 'failed') {
                showMessage('error', `카드 등록 실패: ${params.get('msg') || '다시 시도해주세요'}`);
                window.history.replaceState({}, '', '/subscription');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // 서버 견적 조회 (대표 계정 기준 금액·첫 결제일·면제 여부). 실패하면 결제 동의를 막는다(금액을 확정할 수 없으므로)
    const loadQuote = async () => {
        if (isStaff) return; // 직원 계정은 결제 불가 — 견적 불필요 (본인 요금표 있으면 직원 아님)
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const res = await httpsCallable(fns, 'getSubscriptionQuote')({});
            const q = res?.data || null;
            if (!q || typeof q.amount !== 'number') throw new Error('견적 응답 형식 오류');
            // 요금 정보 확인 실패(읽기 실패·알 수 없는 요금표)는 '대상 아님'이 아니다 — 결제·안내 모두 막고 오류로 표시
            if (q.subscribable === false && QUOTE_UNAVAILABLE_REASONS.includes(q.reason)) {
                console.error('구독 견적 — 요금 정보 확인 실패:', q.reason);
                setQuote(null);
                setQuoteError(BILLING_ERROR_TEXT.billing_unavailable);
                return;
            }
            setQuote(q);
            setQuoteError('');
        } catch (e) {
            console.error('구독 견적 조회 실패:', e);
            setQuote(null);
            const code = billingErrorCode(e);
            setQuoteError((code && BILLING_ERROR_TEXT[code]) || '결제 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
    };

    // 🧩 플랜 변경 (2026-10-30, 계획_구독플랜제 §1) — 서버 callable setLabPlan({ code }) → { code, amount }. 기공소 대표만, 3코드만.
    //   다음 결제부터 적용(일할 없음). 금액이 바뀌면 기존 동의와 달라지므로 동의 체크를 풀고 견적을 다시 읽는다(quote_changed 재동의 흐름 그대로).
    const handleChangePlan = async (plan) => {
        if (!plan?.code || changingPlan) return;
        if (!window.confirm(`${plan.label} 플랜(월 ${plan.amount.toLocaleString('ko-KR')}원, 부가세 포함 · 지그 월 ${plan.jigLimit.toLocaleString('ko-KR')}개)으로 변경할까요?\n다음 결제부터 적용됩니다.`)) return;
        setChangingPlan(plan.code);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const res = await httpsCallable(fns, 'setLabPlan')({ code: plan.code });
            const d = res?.data || {};
            setConsentChecked(false);
            await loadQuote();
            jigUsage.reload();
            showMessage('success', `${plan.label} 플랜으로 변경되었습니다 (월 ${(typeof d.amount === 'number' ? d.amount : plan.amount).toLocaleString('ko-KR')}원). 다음 결제부터 적용됩니다.`);
        } catch (e) {
            console.error('[구독] 플랜 변경 실패:', e?.code || e?.details?.code || e?.message || e);
            showMessage('error', billingErrorMessage(e, '플랜 변경에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
        } finally {
            setChangingPlan('');
        }
    };

    // 결제 동의 기록 (recordSubscriptionConsent, 계획 S10) — 서버가 견적을 다시 계산해 subscriptionConsents에 기록.
    // expectedAmount = 화면에 보인 금액. 서버 견적과 다르면 서버가 quote_changed 로 거부한다(클라이언트 금액은 기록에 쓰지 않음).
    const recordConsent = async () => {
        if (!consentChecked) throw new Error('자동결제 조건 확인 및 정기결제 동의에 체크해 주세요.');
        if (!quote) throw new Error(quoteError || '결제 정보를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.');
        const fns = getFunctions(undefined, 'asia-northeast3');
        const res = await httpsCallable(fns, 'recordSubscriptionConsent')({
            agreed: true,
            expectedAmount: quote.amount,
            consentTextVersion: quote.consentTextVersion || CONSENT_TEXT_VERSION,
        });
        if (!res?.data?.success) throw new Error('결제 동의 기록에 실패했습니다. 다시 시도해 주세요.');
        // 서버가 돌려준 견적(동의 기록 기준)으로 화면 값을 맞춘다
        if (res.data.quote && typeof res.data.quote.amount === 'number') setQuote(res.data.quote);
    };

    // 서버 오류 → 화면 문구. 동의가 무효가 된 오류면 체크를 풀고 견적을 다시 읽어 바뀐 금액을 보여준다
    const billingErrorMessage = (e, fallback) => {
        const code = billingErrorCode(e);
        if (code && RECONSENT_CODES.includes(code)) {
            setConsentChecked(false);
            loadQuote();
        }
        if (code && PAYMENT_CHECK_CODES.includes(code)) {
            // 결제가 됐을 수도 있음 — 구독·결제내역·카드를 다시 읽고, 재결제 버튼을 잠시 막는다
            const until = Date.now() + PAY_RETRY_LOCK_MS;
            setPayLockUntil(until);
            setTimeout(() => setPayLockUntil(cur => (cur === until ? 0 : cur)), PAY_RETRY_LOCK_MS + 50);
            setPayCheckNeeded(true);
            loadSubscriptionData();
            loadBillingKey();
        }
        if (code === 'pg_not_active') loadPgSetting();
        return (code && BILLING_ERROR_TEXT[code]) || e?.message || fallback;
    };
    // 결과 확인 중·준비 중 오류는 "실패" 접두 없이 그대로 보여준다
    const isNotFailure = (e) => NOT_FAILURE_CODES.includes(billingErrorCode(e));

    const loadTrialRequest = async () => {
        try {
            const q = query(
                collection(db, 'freeTrialRequests'),
                where('userId', '==', user.uid)
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
                // 가장 최신 신청 찾기
                const sorted = snap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
                setTrialRequest(sorted[0]);
            }
        } catch (e) { console.log('무료체험 신청 조회:', e); }
    };

    // eslint-disable-next-line no-unused-vars -- 보존(미사용) 코드, 요금 변경 작업 범위 밖
    const handleRequestTrial = async () => {
        if (requestingTrial) return;
        setRequestingTrial(true);
        try {
            await addDoc(collection(db, 'freeTrialRequests'), {
                userId: user.uid,
                userName: user.name || user.displayName || '',
                companyName: user.companyName || user.businessName || '',
                businessType: businessType || '',
                email: user.email || '',
                phone: user.phone || '',
                status: 'pending',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            showMessage('success', '무료 이용권 신청이 완료되었습니다! 관리자 승인 후 이용 가능합니다.');
            loadTrialRequest();
        } catch (e) {
            console.error('무료체험 신청 실패:', e);
            showMessage('error', '신청 중 오류가 발생했습니다.');
        }
        setRequestingTrial(false);
    };

    const loadBillingKey = async () => {
        try {
            const billingDoc = await getDoc(doc(db, 'billingKeys', user.uid));
            if (billingDoc.exists() && billingDoc.data().status === 'active') {
                setBillingKey(billingDoc.data());
            }
        } catch (e) { console.log('빌키 조회 실패:', e); }
    };

    const loadSubscriptionData = async () => {
        try {
            setLoading(true);

            const subQuery = query(
                collection(db, 'appSubscriptions'),
                where('userId', '==', user.uid),
                where('status', 'in', ['active', 'trial', 'past_due', 'cancelled', 'expired'])
            );
            const subSnapshot = await getDocs(subQuery);

            let activeSub = null;
            if (!subSnapshot.empty) {
                const expMs = (d) => quoteDate(d.expiresAt)?.getTime() || 0;
                // 나머지(해지·만료)는 만료가 늦은 것 = 가장 최근 구독
                const docs = subSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => expMs(b) - expMs(a));
                // active 는 이용권이 살아 있는 것(isLiveSub — 결제 유예 7일 포함)을 먼저 고른다.
                //   만료·유예가 지난 active(결제수단 없음·결제 실패 방치)는 '이용 중지(결제 필요)'로 보이도록 뒤로 미룬다.
                const nowDate = new Date();
                activeSub = docs.find(d => d.status === 'active' && isLiveSub(d, nowDate))
                    || docs.find(d => d.status === 'trial' && isLiveSub(d, nowDate))
                    || docs.find(d => d.status === 'past_due')
                    || docs.find(d => d.status === 'active')
                    || docs.find(d => d.status === 'trial')
                    || docs[0];
                setSubscription(activeSub);
            }

            // 결제 내역 — KISPG 빌키 결제는 paymentHistory에, 구형 결제는 servicePayments에 기록됨 (병합 조회)
            const [legacySnap, kispgSnap] = await Promise.all([
                getDocs(query(
                    collection(db, 'servicePayments'),
                    where('userId', '==', user.uid),
                    where('serviceType', '==', 'app-subscription')
                )),
                getDocs(query(
                    collection(db, 'paymentHistory'),
                    where('userId', '==', user.uid),
                    // 즉시 결제(subscription) + 매월 자동결제(auto_renewal, 스케줄러) — 실패 기록(auto_renewal_failed)은 제외
                    where('type', 'in', ['subscription', 'auto_renewal'])
                )),
            ]);
            const history = [
                ...legacySnap.docs.map(d => ({ id: d.id, source: 'legacy', ...d.data() })),
                ...kispgSnap.docs.map(d => ({ id: d.id, source: 'kispg', ...d.data() })),
            ]
                .sort((a, b) => {
                    const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                    const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                    return dateB - dateA;
                });
            setPaymentHistory(history);
            return { sub: activeSub, history };
        } catch (error) {
            console.error('\uAD6C\uB3C5 \uC815\uBCF4 \uB85C\uB4DC \uC2E4\uD328:', error);
            showMessage('error', '\uAD6C\uB3C5 \uC815\uBCF4\uB97C \uBD88\uB7EC\uC624\uB294\uB370 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.');
            return null;
        } finally {
            setLoading(false);
        }
    };

    // ms: 표시 시간 — 결제 확인 중 안내(연락처 포함)는 길게 보여준다
    const showMessage = (type, text, ms = 5000) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(cur => (cur.text === text ? { type: '', text: '' } : cur)), ms);
    };
    const LONG_MSG_MS = 20000;

    const formatNumber = (num) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return '-';
        const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
        return date.getFullYear() + '\uB144 ' + (date.getMonth() + 1) + '\uC6D4 ' + date.getDate() + '\uC77C';
    };

    // 결제 유예 (BILLING_GRACE_DAYS=7, 대표 결정 2026-10-04): 자동결제가 안 된 active 구독은 만료 후 7일까지 이용 가능
    const graceInfo = getGraceInfo(subscription);
    const graceFailed = graceInfo.inGrace && (Number(subscription?.failedAttempts || 0) > 0 || !!subscription?.lastFailureMsg);
    // 환불(결제 취소)로 끝난 구독 — 서버 endSubscriptionsForRefund: status cancelled + cancelReason payment_cancelled(_webhook) + endedByRefund
    const endedByRefund = subscription?.status === 'cancelled' && (subscription?.endedByRefund === true
        || String(subscription?.cancelReason || '').startsWith('payment_cancelled'));
    // 정상 이용 중인 active 구독 (유예 중이면 결제 화면을 보여 카드 변경·재결제를 유도)
    const activeOk = subscription?.status === 'active' && isLiveSub(subscription) && !graceInfo.inGrace;
    // 상태값은 active 인데 이용권이 끝난 구독(만료 + 유예 7일 경과, 또는 만료일 없음) — '활성'이 아니라 '이용 중지(결제 필요)'
    const activeStopped = subscription?.status === 'active' && !isLiveSub(subscription);

    const getStatusBadge = () => {
        if (!subscription) return null;
        const { status } = subscription;
        // 결제 유예 중(active·만료 후 7일 이내)·환불 종료는 상태값보다 실제 상황을 먼저 보여준다
        const special = graceInfo.inGrace ? { label: '결제 확인 필요', bg: '#fef3c7', color: '#92400e' }
            : activeStopped ? { label: '이용 중지(결제 필요)', bg: '#fee2e2', color: '#991b1b' }
            : endedByRefund ? { label: '환불·종료', bg: '#f1f5f9', color: '#475569' }
            : null;
        const badgeMap = {
            active: { label: '활성', bg: '#d1fae5', color: '#065f46' },
            trial: { label: '\uBB34\uB8CC\uCCB4\uD5D8', bg: '#dbeafe', color: '#1e40af' },
            cancelled: { label: '\uD574\uC9C0\uB428', bg: '#fee2e2', color: '#991b1b' },
            expired: { label: '\uB9CC\uB8CC\uB428', bg: '#fef3c7', color: '#92400e' },
            past_due: { label: '결제 실패', bg: '#fee2e2', color: '#991b1b' },
        };
        const badge = special || badgeMap[status] || badgeMap.cancelled;
        return (
            <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '700',
                backgroundColor: badge.bg,
                color: badge.color
            }}>
                {badge.label}
            </span>
        );
    };

    // 상태 카드의 결제일 칸 — 실제로 다음 결제가 있는 경우(카드 연결된 active 구독)에만 '다음 결제일'을 보인다.
    // 서버 규칙: 다음 결제일 = 이용기간 종료(expiresAt, 23:59:59 KST) 다음 날 (billing-policy chargeDateAfter)
    const getBillingDateCell = () => {
        if (!subscription) return { label: '다음 결제일', value: '-' };
        const exp = quoteDate(subscription.expiresAt);
        const { status } = subscription;
        if (graceInfo.inGrace) {
            return { label: '카드 확인 기한', value: `${formatDate(graceInfo.graceUntil)}까지` };
        }
        if (endedByRefund) return { label: '다음 결제일', value: '다음 결제 없음 (환불로 종료)' };
        // 이용 중지된 active — 지난 날짜를 '다음 결제일'로 보이지 않는다
        if (activeStopped) {
            return { label: '이용 상태', value: exp ? `이용 중지 — 결제가 필요합니다 (이용기간 ${formatDate(exp)} 종료)` : '이용 중지 — 결제가 필요합니다' };
        }
        if (status === 'active') {
            if (billingKey && exp) return { label: '다음 결제일', value: formatDate(new Date(exp.getTime() + 1000)) };
            return { label: '다음 결제일', value: exp ? `다음 결제 없음 (카드 미등록 · ${formatDate(exp)}까지 이용)` : '다음 결제 없음 (카드 미등록)' };
        }
        if (status === 'trial') {
            return { label: isLegacyLabFree ? '무료 기간 종료일' : '체험 종료일', value: exp ? formatDate(exp) : '-' };
        }
        if (status === 'cancelled' && exp && exp.getTime() > Date.now()) {
            return { label: '다음 결제일', value: `다음 결제 없음 (${formatDate(exp)}까지 이용)` };
        }
        return { label: '다음 결제일', value: '다음 결제 없음' };
    };

    // 토스 카드 등록 (결제창 billingAuth) — pgProvider='toss'일 때만 진입
    const handleRegisterCardToss = async () => {
        try {
            setProcessing(true);
            const functions = getFunctions(undefined, 'asia-northeast3');
            const createParams = httpsCallable(functions, 'tossCreateBillingAuthParams');
            // 복귀 주소 = 지금 접속한 주소(서버 허용 목록만, 10-15) — 로그인 세션이 같은 곳으로 돌아와 첫 결제가 이어지게
            const result = await createParams({ returnOrigin: window.location.origin });
            if (!result.data?.success) throw new Error('카드 등록 준비에 실패했습니다.');
            const { clientKey, customerKey, successUrl, failUrl } = result.data;
            const TossPayments = await loadTossSdk();
            const payment = TossPayments(clientKey).payment({ customerKey });
            // 결제창(전체 페이지 리다이렉트) — 복귀는 tossBillingReturn → /subscription?billing=...&pg=toss
            await payment.requestBillingAuth({
                method: 'CARD',
                successUrl,
                failUrl,
                customerEmail: user.email || undefined,
                customerName: user.name || user.businessName || undefined,
            });
        } catch (error) {
            // 사용자가 결제창을 닫은 경우(PAY_PROCESS_CANCELED)는 조용히 복귀
            if (error?.code !== 'PAY_PROCESS_CANCELED') {
                console.error('토스 카드 등록 오류:', error);
                showMessage('error', billingErrorMessage(error, '카드 등록에 실패했습니다.'), isNotFailure(error) ? LONG_MSG_MS : 5000);
            }
            setProcessing(false);
        }
    };

    // KISPG 빌키 발급 (카드 등록)
    const handleRegisterCard = async () => {
        // 개시일 전 차단 (심사용 계정 포함) — 동의 기록·결제창 호출 전에 막는다
        if (!cardRegOpen) {
            showMessage('error', cardRegClosedText);
            return;
        }
        if (isStaff) {
            showMessage('error', '직원 계정은 결제할 수 없습니다. 업체 대표에게 문의하세요.');
            return;
        }
        // 결제 동의 기록이 성공해야만 카드 등록 진행 (서버 tossCreateBillingAuthParams도 동의 기록 없으면 거부 — 계획 S5)
        try {
            setProcessing(true);
            await recordConsent();
        } catch (e) {
            console.error('결제 동의 기록 실패:', e);
            showMessage('error', billingErrorMessage(e, '결제 동의 기록에 실패했습니다.'));
            setProcessing(false);
            return;
        }
        if (pgProvider === 'toss') {
            await handleRegisterCardToss();
            return;
        }
        try {
            setProcessing(true);
            const functions = getFunctions(undefined, 'asia-northeast3');
            const createParams = httpsCallable(functions, 'kispgCreateBillingParams');

            // ordNo는 서버에서 생성 (KIS 40자 제한 대응)
            const result = await createParams({
                goodsNm: `카드 등록 (덴탈클러스터 ${isClinic ? '치과' : '기공소'} 정기결제용)`,
                goodsAmt: displayAmount,
                ordNm: user.name || user.businessName || '',
                ordTel: (user.phone || '').replace(/[^0-9]/g, ''),
                ordEmail: user.email || '',
                returnUrl: `https://asia-northeast3-dental-lab-system.cloudfunctions.net/kispgBillingReturn`,
            });

            if (result.data.success) {
                // KISPG 결제창으로 form submit — PC는 iframe 모달, 모바일은 전체 페이지 이동
                const { params, authUrl } = result.data;
                // 2026-07-30: KISPG 결제창이 iframe(교차 출처) 접근을 차단해 빈 화면이 되는 것 확인
                // (콘솔: "Blocked a frame with origin https://api.kispg.co.kr ...") — PC도 전체 페이지 이동으로 통일
                const isMobile = true;
                const submitForm = (target) => {
                    const form = document.createElement('form');
                    form.method = 'POST';
                    form.action = authUrl;
                    form.acceptCharset = 'UTF-8';
                    if (target) form.target = target;
                    Object.entries(params).forEach(([key, value]) => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = key;
                        input.value = value || '';
                        form.appendChild(input);
                    });
                    document.body.appendChild(form);
                    form.submit();
                    document.body.removeChild(form);
                };
                if (isMobile) {
                    submitForm(null); // 전체 페이지 이동 (returnUrl에서 /subscription으로 복귀)
                } else {
                    setShowCardModal(true);
                    // 모달의 iframe이 렌더된 뒤 그 iframe을 타겟으로 제출
                    setTimeout(() => { submitForm('kispgBillingFrame'); setProcessing(false); }, 150);
                }
            }
        } catch (error) {
            console.error('카드 등록 오류:', error);
            showMessage('error', billingErrorMessage(error, '카드 등록에 실패했습니다.'), isNotFailure(error) ? LONG_MSG_MS : 5000);
            setProcessing(false);
        }
    };

    // KISPG 빌키 결제 (구독 시작)
    const handleSubscribe = async () => {
        if (isStaff) {
            showMessage('error', '직원 계정은 결제할 수 없습니다. 업체 대표에게 문의하세요.');
            return;
        }

        if (!billingKey) {
            showMessage('error', '먼저 카드를 등록해주세요.');
            return;
        }

        // 확인창(기록 없음) 대신 결제 동의 체크 + 서버 기록 (법무검토 ④ P1 — 동의 입증)
        try {
            setProcessing(true);
            await recordConsent();
            const functions = getFunctions(undefined, 'asia-northeast3');
            // 등록된 카드의 결제사에 맞는 청구 함수 선택 (toss 병렬 경로)
            const chargeFn = billingKey?.provider === 'toss' ? 'tossBillingCharge' : 'kispgBillingCharge';
            const charge = httpsCallable(functions, chargeFn);
            const result = await charge({ userId: user.uid });

            if (result.data.success) {
                if (result.data.trial) {
                    // 이용권 남음(체험·무료기간·이미 결제된 기간) — 카드만 연결. 첫 결제일은 서버 firstChargeDate
                    const loaded = await loadSubscriptionData();
                    showMessage('success', linkedMessage(result.data, loaded, false), 10000);
                } else {
                    showMessage('success', '구독이 시작되었습니다! 모든 기능을 이용하실 수 있습니다.');
                    await loadSubscriptionData();
                }
            }
        } catch (error) {
            console.error('구독 결제 실패:', error);
            showMessage('error', billingErrorMessage(error, error.error_msg || '결제 처리 중 오류가 발생했습니다.'), isNotFailure(error) ? LONG_MSG_MS : 5000);
        } finally {
            setProcessing(false);
        }
    };

    // \uCE74\uB4DC \uB4F1\uB85D \uC9C1\uD6C4 \uC790\uB3D9 \uCCAD\uAD6C (\uBC29\uC2DD B: \uCE74\uB4DC \uB4F1\uB85D \u2192 \uC989\uC2DC \uCCAB \uACB0\uC81C)
    const autoChargeAfterCardRegister = async (provider) => {
        try {
            setProcessing(true);
            const functions = getFunctions(undefined, 'asia-northeast3');
            // provider 인자(복귀 파라미터) 우선, 없으면 저장된 카드의 결제사
            const useToss = provider === 'toss' || (!provider && billingKey?.provider === 'toss');
            const charge = httpsCallable(functions, useToss ? 'tossBillingCharge' : 'kispgBillingCharge');
            const result = await charge({ userId: user.uid });
            if (result.data?.success) {
                if (result.data.trial) {
                    await loadBillingKey();
                    const loaded = await loadSubscriptionData();
                    showMessage('success', linkedMessage(result.data, loaded, true), 10000);
                    return;
                } else {
                    showMessage('success', `\uD83C\uDF89 \uAD6C\uB3C5\uC774 \uC2DC\uC791\uB418\uC5C8\uC2B5\uB2C8\uB2E4! ${(result.data.amount || displayAmount).toLocaleString()}\uC6D0 \uACB0\uC81C \uC644\uB8CC`);
                }
                await loadBillingKey();
                await loadSubscriptionData();
            } else {
                showMessage('error', '\uCE74\uB4DC\uB294 \uB4F1\uB85D\uB418\uC5C8\uC73C\uB098 \uCCAB \uACB0\uC81C\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. "\uAD6C\uB3C5 \uC2DC\uC791" \uBC84\uD2BC\uC73C\uB85C \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.');
                await loadBillingKey();
            }
        } catch (error) {
            console.error('자동 청구 실패:', error);
            // 결과 확인 중·준비 중 오류(결제가 됐을 수도 있음)는 "실패" 접두를 붙이지 않는다 — 문구가 "다시 결제하지 마세요"와 모순
            const msg = billingErrorMessage(error, '다시 시도해주세요');
            if (isNotFailure(error)) {
                showMessage('error', `카드는 등록되었습니다. ${msg}`, LONG_MSG_MS);
            } else {
                showMessage('error', `카드는 등록되었으나 자동결제 연결에 실패했습니다: ${msg}`);
            }
            await loadBillingKey();
        } finally {
            setProcessing(false);
        }
    };

    // 서버 결제 응답의 첫 결제일(firstChargeDate 'YYYY-MM-DD') → 화면 날짜. 없으면 견적의 첫 결제일
    const chargeDateText = (data) => {
        const d = quoteDate(data?.firstChargeDate) || quoteDate(quote?.firstChargeDate);
        return d ? formatDate(d) : '다음 결제일';
    };

    // 카드 연결({trial:true}) 응답 문구 — 이용권이 '무료'인지 '이미 결제된 기간'인지 구분한다.
    //   서버는 체험·무료기간·결제 기간(결제 사고 복구 직후 포함) 모두 trial:true 로 돌려주므로, 결제한 고객에게
    //   "무료 체험 중" 문구가 뜨지 않도록 결제내역(periodEnd 가 남은 미취소 결제)·구독 문서로 판정한다.
    const linkedMessage = (data, loaded, afterCardRegister) => {
        const now = Date.now();
        const sub = loaded?.sub || null;
        const paid = (loaded?.history || []).some(h => h.source === 'kispg' && h.status !== 'cancelled'
            && (quoteDate(h.periodEnd)?.getTime() || 0) > now)
            || (!!sub?.lastPaymentTid && !sub?.trialGranted && !sub?.freePeriodSource);
        const quoteFree = (quoteDate(quote?.trialEndsAt)?.getTime() || 0) > now;
        const free = !paid && (quoteFree || sub?.trialGranted === true || !!sub?.freePeriodSource);
        const when = `${chargeDateText(data)}에 ${formatNumber(data.quoteAmount || displayAmount)}원`;
        const head = afterCardRegister ? '🎉 카드 등록 완료! ' : '🎉 자동결제가 연결되었습니다. ';
        if (free) {
            return afterCardRegister
                ? `${head}${freeLabel}이 이어집니다. 첫 자동결제는 ${when}입니다.`
                : `${head}${freeLabel} 중에는 결제되지 않으며, 첫 자동결제는 ${when}입니다.`;
        }
        return `${head}남은 이용 기간에는 추가로 결제되지 않으며, 다음 자동결제는 ${when}입니다.`;
    };

    // 무료 체험 진행 중 여부 (배너·문구용)
    const trialActive = subscription?.trialGranted && subscription?.status !== 'cancelled' &&
        (subscription?.expiresAt?.toDate ? subscription.expiresAt.toDate().getTime() > Date.now() : false);

    // 무료 체험 만료 여부 (체험을 받았고, 지금은 유효하지 않으며, 카드 미등록)
    const trialExpired = !!subscription?.trialGranted && !trialActive &&
        subscription?.status !== 'active' && subscription?.status !== 'cancelled' && !billingKey;

    // ── 약관 v6.2 결제 화면 값 (서버 견적 우선, 견적 전에는 pricing.js 표시용 값) ──
    // 🧩 플랜제(2026-10-30): 현재 플랜 = 서버 견적 tariffCode(→ 구독 문서 tariffCode) 를 planV8At 규칙으로 본 플랜. 모르면 null(카드에 '현재' 표시 없음).
    //   옛 33,000원 요금표는 planV8At 전까지 자기 금액 그대로(planForCode 가 그대로 돌려줌).
    const currentPlan = planForCode(quote?.tariffCode || subscription?.tariffCode || null, featuresDoc);
    const currentPlanCode = currentPlan?.code || null;
    // 견적 전·전면 무료(amount 0) 표시용 기본 금액 — 기공소는 현재 플랜(없으면 베이직), 치과는 종전 표시값
    const fallbackAmount = isLab ? (currentPlan?.amount || DEFAULT_LAB_PLAN.amount) : pricing.totalAmount;
    const displayAmount = (quote && typeof quote.amount === 'number' && quote.amount > 0) ? quote.amount : fallbackAmount;
    // (displaySupply·displayVat 는 플랜 카드가 플랜별로 계산하므로 제거 — 2026-10-30)
    // 청구 대상 아님: 서버 견적 subscribable=false (면제·기존 치과·관리자·해외·구독 대상 아닌 유형·전원 무료).
    //   요금 정보 확인 실패 사유는 loadQuote 에서 오류로 돌리므로 여기 오지 않는다.
    const noChargeReason = user?.subscriptionExempt === true ? 'exempt'
        : (quote && quote.subscribable === false) ? (quote.exempt === true ? 'exempt' : (quote.reason || 'not_subject'))
        : null;
    const noCharge = !!noChargeReason;
    const noChargeText = {
        exempt: '이 계정은 구독료 면제 대상입니다. 면제가 바뀌게 되면 최소 30일 전에 개별로 알려드립니다.',
        legacy_clinic: '기존 가입 치과는 별도 안내 전까지 구독료 없이 모든 기능을 이용합니다. 바뀌게 되면 최소 30일 전에 개별로 알려드립니다.',
        clinic_free: '치과는 무료입니다. 구독료 없이 모든 기능을 이용합니다. 바뀌게 되면 최소 30일 전에 개별로 알려드립니다.',   // 2026-10-30 대표 결정(치과 무료)
        admin: '관리자 계정은 구독료 청구 대상이 아닙니다.',
        overseas: '해외 기공소 계정은 월 구독료 청구 대상이 아닙니다.',
        not_subject: '이 계정 유형은 월 구독료 청구 대상이 아닙니다.',
        all_free: '지금은 전면 무료로 제공 중입니다. 유료 전환 시에는 최소 30일 전에 미리 안내드립니다.',
    }[noChargeReason] || '이 계정은 지금 구독료 청구 대상이 아닙니다.';
    // 기존 국내 기공소(요금표 없음)의 2026-12-31까지 무료는 '무료 체험'이 아니라 '기존 회원 무료 기간' (검토 P2-7)
    const isLegacyLabFree = !isClinic && quote?.reason === 'legacy_lab';
    const freeLabel = isLegacyLabFree ? '기존 회원 무료 기간' : '무료 체험';
    const firstChargeDateObj = quoteDate(quote?.firstChargeDate);
    const trialEndsObj = quoteDate(quote?.trialEndsAt);
    const chargeDay = quote?.chargeDay || (firstChargeDateObj ? firstChargeDateObj.getDate() : null);
    const chargeDayText = chargeDay ? `매월 ${chargeDay}일${chargeDay > 28 ? '(해당 날짜가 없는 달은 말일)' : ''}` : '매월 같은 날';
    const firstChargeText = firstChargeDateObj ? formatDate(firstChargeDateObj) : '-';
    const trialEndsText = trialEndsObj && trialEndsObj.getTime() > Date.now() ? formatDate(trialEndsObj) : null;

    // 클라이언트 체험 시작(handleStartTrial) 삭제 — 약관 v6.2: 체험은 서버(onUserCreated)만 부여, 사업자당 1회.
    //   rules R3(appSubscriptions 본인 create 봉쇄)과 세트 (계획 W9(d)). 31일마다 새 체험을 만드는 우회 차단.

    // 카드 삭제 (빌링키 해제) — KISPG 빌키 삭제 + Firestore 상태 갱신
    const handleDeleteCard = async () => {
        if (!billingKey) return;
        if (!window.confirm('등록된 카드를 삭제할까요?\n삭제하면 자동결제가 중단되며, 다시 등록해야 결제할 수 있습니다.')) return;
        try {
            setProcessing(true);
            const functions = getFunctions(undefined, 'asia-northeast3');
            const del = httpsCallable(functions, billingKey?.provider === 'toss' ? 'tossDeleteBillingKey' : 'kispgDeleteBillingKey');
            await del({});
            setBillingKey(null);
            showMessage('success', '카드가 삭제되었습니다.');
        } catch (e) {
            console.error('카드 삭제 실패:', e);
            showMessage('error', e.message || '카드 삭제에 실패했습니다.');
        } finally {
            setProcessing(false);
        }
    };

    // [결제 확인] — 결제가 됐을 수도 있는 오류 뒤, 서버(recoverMyPayment)가 결제사 결과를 다시 확인해 기록을 복구한다
    //   계약(dev-server 2026-10-04): 입력 없음, 출력 { recovered:boolean, pending:boolean }
    const handleRecoverPayment = async () => {
        if (payChecking) return;
        setPayChecking(true);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const res = await httpsCallable(fns, 'recoverMyPayment')({});
            const d = res?.data || {};
            if (d.recovered === true) {
                setPayCheckNeeded(false);
                setPayLockUntil(0);
                await loadBillingKey();
                await loadSubscriptionData();
                showMessage('success', '결제가 확인되었습니다.', 10000);
            } else if (d.pending === true) {
                showMessage('error', '아직 확인 중입니다. 잠시 후 다시 눌러 주세요.', LONG_MSG_MS);
            } else {
                // recovered·pending 모두 false — 복구할 결제가 없음(계약상 의미 미확정). 최신 상태를 다시 읽고 안내
                console.warn('[구독] 결제 확인 — 복구할 결제 없음 응답:', d);
                setPayCheckNeeded(false);
                await loadSubscriptionData();
                showMessage('error', '확인 중인 결제를 찾지 못했습니다. 결제 내역을 확인해 주시고, 문제가 있으면 010-2972-3733으로 연락해 주세요.', LONG_MSG_MS);
            }
        } catch (e) {
            console.error('[구독] 결제 확인(recoverMyPayment) 실패:', e?.details?.code || '', e?.message || e);
            showMessage('error', '결제 확인에 실패했습니다. 다시 결제하지 마시고 고객센터(010-2972-3733)로 연락해 주세요.', LONG_MSG_MS);
        } finally {
            setPayChecking(false);
        }
    };

    // 본인 환불 이력 — 서버가 본인 취소 시 paymentHistory.cancelledBy = 본인 uid 로 기록 (관리자 취소=관리자 uid, 토스 웹훅='toss_webhook')
    //   계정당 1회 제한(대표 결정 2026-10-04): 이력이 있으면 버튼 대신 고객센터 안내. 최종 판정은 서버(self_refund_limit)
    const hasSelfRefund = paymentHistory.some(h => h.status === 'cancelled' && !!user?.uid && h.cancelledBy === user.uid);

    // 결제 취소 (전액 환불) — 결제 후 7일 이내, KISPG 결제(paymentHistory)만
    const canCancelPayment = (item) => {
        if (item.source !== 'kispg' || item.status === 'cancelled' || !item.tid) return false;
        const paidAt = item.createdAt?.toDate ? item.createdAt.toDate() : null;
        return paidAt && (Date.now() - paidAt.getTime()) <= 7 * 24 * 60 * 60 * 1000;
    };
    const handleCancelPayment = async (item) => {
        if (!window.confirm(`이 결제(${formatNumber(item.amount || 0)}원)를 취소(전액 환불)할까요?\n구독이 바로 종료되며, 남은 이용 기간도 함께 끝납니다.\n화면에서 하는 직접 환불은 계정당 1회입니다. 그 뒤에도 환불·청약철회는 고객센터(010-2972-3733)에서 받습니다.`)) return;
        try {
            setProcessing(true);
            const functions = getFunctions(undefined, 'asia-northeast3');
            const cancel = httpsCallable(functions, item.provider === 'toss' ? 'tossCancelPayment' : 'kispgCancelPayment');
            await cancel({ paymentId: item.id });
            showMessage('success', '결제가 취소되어 구독이 종료되었습니다. 카드사 환불은 2~5일 소요될 수 있습니다.', 10000);
            await loadSubscriptionData();
        } catch (e) {
            console.error('결제 취소 실패:', e);
            const msg = billingErrorMessage(e, '결제 취소에 실패했습니다.');
            showMessage('error', msg, billingErrorCode(e) === 'self_refund_limit' ? LONG_MSG_MS : 5000);
            // 한도 초과면 이력을 다시 읽어 버튼을 안내 문구로 바꾼다
            if (billingErrorCode(e) === 'self_refund_limit') await loadSubscriptionData();
        } finally {
            setProcessing(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!subscription?.id) return;
        // 직원 계정은 대표 구독을 해지할 수 없다 (rules: 해지는 userId 본인·관리자만 — 시스템보안 P2-10)
        if (isStaff) {
            showMessage('error', '구독 해지는 업체 대표 계정에서만 할 수 있습니다.');
            return;
        }

        const confirmed = window.confirm(
            '\uAD6C\uB3C5\uC744 \uD574\uC9C0\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?\n\n\uD604\uC7AC \uACB0\uC81C \uAE30\uAC04\uC774 \uB05D\uB0A0 \uB54C\uAE4C\uC9C0 \uC11C\uBE44\uC2A4\uB97C \uC774\uC6A9\uD558\uC2E4 \uC218 \uC788\uC2B5\uB2C8\uB2E4.'
            // 대표 10-15: 해지를 환불로 오해(대표 실결제 시험) — 환불 경로 1줄. 조건은 같은 화면 결제 취소 확인창·약관 제4조의3 제5항과 같게
            + '\n\n해지는 다음 결제를 멈추는 것이며 환불이 아닙니다. 환불은 결제 내역의 [결제 취소](결제 후 7일 이내, 화면에서 1회) 또는 고객센터(010-2972-3733)에서 할 수 있습니다.'
        );

        if (!confirmed) return;

        try {
            setProcessing(true);
            await updateDoc(doc(db, 'appSubscriptions', subscription.id), {
                status: 'cancelled',
                cancelledAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            setSubscription(prev => ({ ...prev, status: 'cancelled' }));
            showMessage('success', '\uAD6C\uB3C5\uC774 \uD574\uC9C0\uB418\uC5C8\uC2B5\uB2C8\uB2E4. \uD604\uC7AC \uAE30\uAC04\uC774 \uB9CC\uB8CC\uB420 \uB54C\uAE4C\uC9C0 \uC774\uC6A9 \uAC00\uB2A5\uD569\uB2C8\uB2E4.');
        } catch (error) {
            console.error('\uAD6C\uB3C5 \uD574\uC9C0 \uC2E4\uD328:', error);
            showMessage('error', '\uAD6C\uB3C5 \uD574\uC9C0 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.');
        } finally {
            setProcessing(false);
        }
    };

    // 기능 목록은 pricing.js에서 동적으로 조회 (치과: 구독 잠금 9기능 / 기공소: 명세서 자동화)
    const featureIcons = pricing.features;
    // 치과 잠금 표시용 — 구독(active)·체험(trial)·면제 계정이면 '이용 중', 아니면 '구독 시 열림'
    // 시행 전에는 실제 잠금이 없지만(전 기능 무료) 페이지에서 구독 가치를 미리 보여준다 (2026-08-21 대표 지시)
    //   판정은 billingPolicy.isLiveSub 한 곳 (App.js 잠금과 같은 규칙 — 해지 후 남은 기간·결제 유예 7일 포함)
    //   2026-10-30 대표 결정: 치과는 무료 — 잠금 배지 없이 항상 '이용 중'
    const clinicUnlocked = isClinic || user?.subscriptionExempt === true || isLiveSub(subscription);

    const billingDateCell = getBillingDateCell();

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p style={{ color: '#64748b', fontSize: '14px' }}>{'\uAD6C\uB3C5 \uC815\uBCF4\uB97C \uBD88\uB7EC\uC624\uB294 \uC911...'}</p>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // \uC804\uBA74 \uBB34\uB8CC (\uB300\uD45C \uD655\uC815 2026-08-24): \uACB0\uC81C\u00B7\uCE74\uB4DC \uC548\uB0B4 \uC804\uCCB4 \uC228\uAE40 \u2014 \uC720\uB8CC\uD654 \uC7AC\uAC1C(allFree=false) \uC2DC \uC544\uB798 \uC6D0\uB798 \uD654\uBA74 \uC790\uB3D9 \uBCF5\uADC0
    // 대표 10-15: 「전면 무료」 전체 화면 안내 대신 일반 구독 화면을 안내형으로(가격·기능·약관은 보이고 결제 버튼 자리에 1줄). 서버는 allFree 동안 결제를 거절한다
    if (false && allFreeOn) {
        return (
            <div style={{ maxWidth: 560, margin: '60px auto', padding: '0 20px', textAlign: 'center' }}>
                <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 16, padding: '40px 32px' }}>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>{'\uD83C\uDF89'}</div>
                    <div style={{ fontSize: 19, fontWeight: 800, color: '#0F172A', marginBottom: 8 }}>{'\uC9C0\uAE08\uC740 \uC804\uBA74 \uBB34\uB8CC\uB85C \uC81C\uACF5 \uC911\uC785\uB2C8\uB2E4'}</div>
                    <div style={{ fontSize: 14, color: '#64748B', lineHeight: 1.8 }}>
                        {'\uAC70\uB798\uBA85\uC138\uC11C\u00B7\uC138\uAE08\uACC4\uC0B0\uC11C\u00B7\uB374\uD074 \uD504\uB85C\uADF8\uB7A8\uC744 \uD3EC\uD568\uD55C \uBAA8\uB4E0 \uAE30\uB2A5\uC744'}<br />
                        {'\uACB0\uC81C\uB098 \uCE74\uB4DC \uB4F1\uB85D \uC5C6\uC774 \uC774\uC6A9\uD558\uC2E4 \uC218 \uC788\uC2B5\uB2C8\uB2E4.'}<br />
                        {'\uC720\uB8CC \uC804\uD658 \uC2DC\uC5D0\uB294 \uCD5C\uC18C 30\uC77C \uC804\uC5D0 \uBBF8\uB9AC \uC548\uB0B4\uB4DC\uB9BD\uB2C8\uB2E4.'}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* \uD5E4\uB354 */}
            <div style={styles.header}>
                <div style={styles.headerIcon}>
                    <span style={{ fontSize: '32px' }}>{'\uD83D\uDCF1'}</span>
                </div>
                <div>
                    <h1 style={styles.title}>{'\uAD6C\uB3C5 \uAD00\uB9AC'}</h1>
                    <p style={styles.subtitle}>{pricing.title}</p>
                </div>
            </div>

            {/* \uCE58\uACFC \uAD6C\uB3C5 \uC2DC\uD589 \uC720\uBCF4 \uC548\uB0B4 (2026-08-17 \uC57D\uAD00 v6.0 \u2014 \uC2DC\uD589 \uC804 \uBB34\uCCAD\uAD6C\uB294 \uC11C\uBC84 \uAC00\uB4DC\uAC00 \uBCF4\uC7A5) */}
            {/* 약관 v6.2: 기존 가입 치과·면제 계정 안내 ("영구" 표현 금지 — 법무 ②-1) */}
            {noCharge && noChargeReason !== 'all_free' && (
                <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1D4ED8', borderRadius: 10, padding: '11px 14px', fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
                    {noChargeText}
                </div>
            )}

            {/* \uBA54\uC2DC\uC9C0 */}
            {message.text && (
                <div style={{
                    ...styles.message,
                    ...(message.type === 'success' ? styles.messageSuccess : styles.messageError)
                }}>
                    <span style={{ fontSize: '18px' }}>
                        {message.type === 'success' ? '\u2705' : '\u26A0\uFE0F'}
                    </span>
                    <span>{message.text}</span>
                </div>
            )}

            {/* 결제 확인 — 결제가 됐을 수도 있는 오류 뒤 (재결제 대신 서버 확인) */}
            {payCheckNeeded && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 16px', marginBottom: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
                    <span style={{ fontSize: 14, color: '#92400e' }}>
                        {'결제가 완료됐는지 확인이 필요합니다. 다시 결제하지 마시고 [결제 확인]을 눌러 주세요.'}
                    </span>
                    <button
                        onClick={handleRecoverPayment}
                        disabled={payChecking}
                        style={{ fontSize: 14, fontWeight: 700, color: '#fff', background: '#d97706', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: payChecking ? 'not-allowed' : 'pointer', opacity: payChecking ? 0.6 : 1 }}
                    >
                        {payChecking ? '확인 중...' : '결제 확인'}
                    </button>
                </div>
            )}

            {/* \uD604\uC7AC \uAD6C\uB3C5 \uC0C1\uD0DC \uCE74\uB4DC */}
            {subscription && (
                <div style={styles.statusCard}>
                    <div style={styles.statusCardGradient}>
                        <div style={styles.statusHeader}>
                            <h2 style={styles.statusTitle}>{'\uD604\uC7AC \uAD6C\uB3C5 \uC0C1\uD0DC'}</h2>
                            {getStatusBadge()}
                        </div>
                        <div style={styles.statusDetails}>
                            <div style={styles.statusItem}>
                                <span style={styles.statusLabel}>{billingDateCell.label}</span>
                                <span style={styles.statusValue}>{billingDateCell.value}</span>
                            </div>
                            <div style={styles.statusItem}>
                                <span style={styles.statusLabel}>{'\uC6D4 \uC774\uC6A9\uB8CC'}</span>
                                <span style={styles.statusValue}>
                                    {formatNumber(subscription.totalAmount || displayAmount)}{'\uC6D0'}
                                </span>
                            </div>
                            <div style={styles.statusItem}>
                                <span style={styles.statusLabel}>{'\uAD6C\uB3C5\uC790'}</span>
                                <span style={styles.statusValue}>{user.name || user.email}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

                        {/* 환자앱 마케팅 현황 제거 (2026-08-21) — 폐기된 구 치과 광고 상품(월 22만원)의 잔재 */}

            {/* \uC8FC\uC694 \uAE30\uB2A5 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                    <span style={{ fontSize: '20px' }}>{'\u2B50'}</span>
                    {isClinic ? '구독하면 열리는 기능' : '기공소 거래명세서 자동화'}
                </h2>
                <p style={styles.sectionDesc}>
                    {isClinic
                        ? '치과는 무료입니다 — 아래 기능을 모두 이용할 수 있습니다. 기공물 관리가 전화·수첩에서 화면 하나로 바뀝니다'
                        : 'CAD \uD3F4\uB354\uB9CC \uC5F0\uACB0\uD558\uBA74 \uC218\uC9D1\uBD80\uD130 \uBA85\uC138\uC11C\u00B7\uC138\uAE08\uACC4\uC0B0\uC11C\uAE4C\uC9C0 \uC790\uB3D9\uC73C\uB85C'}
                </p>
                <div style={styles.featureGrid}>
                    {featureIcons.map((feature, idx) => (
                        <div key={idx} style={{
                            ...styles.featureCard,
                            position: 'relative',
                            // 치과 비구독: 잠김 컨셉 — 설명은 살짝 흐려서 "구독하면 열린다"는 궁금증 유발 (2026-08-21 대표 확정)
                            ...(isClinic && !clinicUnlocked ? { background: '#FAFBFF', border: '1px solid #E0E7FF' } : {}),
                        }}>
                            {isClinic && (
                                <span style={{
                                    position: 'absolute', top: 10, right: 10, fontSize: 10.5, fontWeight: 800,
                                    padding: '2px 8px', borderRadius: 6,
                                    ...(clinicUnlocked
                                        ? { background: '#D1FAE5', color: '#059669' }
                                        : { background: '#EEF2FF', color: '#4F46E5' }),
                                }}>{clinicUnlocked ? '✓ 이용 중' : '🔒 구독 시 열림'}</span>
                            )}
                            {/* 설명 블러 제거 (2026-08-21 대표 지적: 판매 페이지에서는 내용이 읽혀야 산다) —
                                블러는 실제 기능 화면의 잠금 게이트(ClinicSubGate)에만. 여기선 배지로만 잠금 표시 */}
                            <div style={styles.featureIconWrap}>
                                <span style={{ fontSize: '28px' }}>{feature.icon}</span>
                            </div>
                            <h3 style={styles.featureTitle}>{feature.title}</h3>
                            <p style={styles.featureDesc}>{feature.desc}</p>
                        </div>
                    ))}
                </div>
                {isClinic && !clinicUnlocked && (
                    <div style={{ marginTop: 12, background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#4338CA', fontWeight: 600, lineHeight: 1.6 }}>
                        {`위 기능은 구독 치과에 열리는 기능입니다 — 월 ${formatNumber(displayAmount)}원(부가세 포함)으로 전부 이용할 수 있습니다.`}
                        <br />{'의뢰서 작성·보낸 의뢰서 확인은 구독하지 않아도 무료입니다.'}
                    </div>
                )}
            </div>

            {/* \uD83E\uDDE9 \uC9C0\uADF8 \uC694\uAE08 \uAD6C\uAC04 + \uC774\uBC88 \uB2EC \uB0B4 \uAD6C\uAC04 \u2014 \uAE30\uACF5\uC18C\u00B7\uC9C0\uADF8 \uCF1C\uC9C4 \uACC4\uC815\uB9CC (2026-10-23, \uC9C0\uADF8 \uD654\uBA74\uC5D0\uC11C \uC774\uB3D9) */}
            <JigUsagePlan user={user} showUsage={false} />   {/* 사용량 막대는 아래 「구독 플랜」 섹션에 한 번만 (2026-10-30) */}

            {/* 요금 안내 — 🧩 구독 플랜제 (2026-10-30, 계획_구독플랜제 §1·§2 대표 결정): 기공소는 플랜 카드 3종 + 지그 사용량 막대, 치과는 무료 안내 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                    <span style={{ fontSize: '20px' }}>{'💳'}</span>
                    {isClinic ? '치과 이용 요금' : '구독 플랜'}
                </h2>
                {isClinic ? (
                    <div style={{ padding: '22px 18px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#065f46', marginBottom: 6 }}>{'치과는 무료입니다'}</div>
                        <div style={{ fontSize: 13.5, color: '#047857', lineHeight: 1.7 }}>{'구독료·카드 등록 없이 모든 기능을 이용할 수 있습니다. 바뀌게 되면 최소 30일 전에 개별로 알려드립니다.'}</div>
                    </div>
                ) : (
                    <>
                        <p style={styles.sectionDesc}>{'세 플랜 모두 전 기능이 열립니다. 차이는 지그(커스텀 치아) 월 한도뿐 — 한도를 넘어도 생성은 멈추지 않고, 다음 달부터 다음 플랜으로 전환됩니다.'}</p>
                        {/* 지그 사용량 — 한도 대비 % 가 주인공, 개수는 작은 글씨 (대표 10-30) */}
                        <div style={{ padding: '14px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 16 }}>
                            <JigUsageBar usage={jigUsage.usage} error={jigUsage.error} loading={jigUsage.loading} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                            {LAB_PLANS.map((plan) => {
                                const isCurrent = plan.code === currentPlanCode;
                                const busy = changingPlan === plan.code;
                                const supply = Math.round(plan.amount * 10 / 11);
                                return (
                                    <div key={plan.code} style={{
                                        position: 'relative', borderRadius: 14, padding: '20px 16px', textAlign: 'center',
                                        background: isCurrent ? '#eef2ff' : '#fff',
                                        border: isCurrent ? '2px solid #6366F1' : '1px solid #e2e8f0',
                                    }}>
                                        {isCurrent && (
                                            <span style={{ position: 'absolute', top: 10, right: 10, fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 6, background: '#6366F1', color: '#fff' }}>{'현재 플랜'}</span>
                                        )}
                                        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>{plan.label}</div>
                                        <div style={{ fontSize: 26, fontWeight: 800, color: '#6366F1', lineHeight: 1.1 }}>
                                            {formatNumber(plan.amount)}<span style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginLeft: 2 }}>{'원/월'}</span>
                                        </div>
                                        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>{`부가세 포함 (공급가 ${formatNumber(supply)}원 + VAT ${formatNumber(plan.amount - supply)}원)`}</div>
                                        <div style={{ margin: '14px 0 10px', fontSize: 13.5, color: '#334155', lineHeight: 1.8 }}>
                                            <div><b>{`지그 월 ${formatNumber(plan.jigLimit)}개`}</b>{' 한도'}</div>
                                            <div>{'전 기능 이용'}</div>
                                        </div>
                                        {/* 변경 버튼 — 대표 계정만(직원 불가). 현재 플랜은 비활성 */}
                                        {!isStaff && (
                                            <button
                                                type="button"
                                                onClick={() => handleChangePlan(plan)}
                                                disabled={isCurrent || !!changingPlan}
                                                style={{
                                                    width: '100%', padding: '10px 0', borderRadius: 10, fontSize: 13.5, fontWeight: 700,
                                                    border: isCurrent ? '1px solid #c7d2fe' : 'none',
                                                    background: isCurrent ? '#fff' : '#6366F1', color: isCurrent ? '#6366F1' : '#fff',
                                                    cursor: (isCurrent || changingPlan) ? 'default' : 'pointer', opacity: (!isCurrent && changingPlan) ? 0.5 : 1,
                                                }}
                                            >
                                                {busy ? '변경 중...' : isCurrent ? '이용 중' : '이 플랜으로 변경'}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <p style={{ margin: '12px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.7 }}>
                            {'플랜 변경은 다음 결제부터 적용됩니다(일할 계산 없음). 금액이 바뀌면 결제 화면에서 자동결제 조건에 다시 동의해 주세요. 프리미엄 한도 초과는 별도로 안내드립니다.'}
                            {isStaff && ' 플랜 변경은 업체 대표 계정에서 할 수 있습니다.'}
                        </p>
                    </>
                )}

                {/* 결제 정보 */}
                {!isClinic && (
                <div style={{ ...styles.billingInfoGrid, marginTop: 16 }}>
                    <div style={styles.billingInfoItem}>
                        <span style={styles.billingInfoIcon}>{'🏢'}</span>
                        <div>
                            <div style={styles.billingInfoLabel}>{'결제 대상'}</div>
                            <div style={styles.billingInfoValue}>{'덴탈클러스터 (과세 사업자)'}</div>
                        </div>
                    </div>
                    <div style={styles.billingInfoItem}>
                        <span style={styles.billingInfoIcon}>{'🧾'}</span>
                        <div>
                            <div style={styles.billingInfoLabel}>{'증빙'}</div>
                            <div style={styles.billingInfoValue}>{'세금계산서 (과세)'}</div>
                        </div>
                    </div>
                    <div style={styles.billingInfoItem}>
                        <span style={styles.billingInfoIcon}>{'🔄'}</span>
                        <div>
                            <div style={styles.billingInfoLabel}>{'결제 방식'}</div>
                            <div style={styles.billingInfoValue}>{'매월 자동 결제'}</div>
                        </div>
                    </div>
                </div>
                )}
            </div>

            {/* \uAD6C\uB3C5 \uBC84\uD2BC */}
            <div style={styles.section}>
                {activeOk ? (
                    <div style={styles.subscribedActions}>
                        <div style={styles.subscribedBanner}>
                            <span style={{ fontSize: '24px' }}>{'\u2705'}</span>
                            <div>
                                <strong style={{ color: '#065f46', fontSize: '16px' }}>{'\uAD6C\uB3C5 \uC911\uC785\uB2C8\uB2E4'}</strong>
                                <p style={{ margin: '4px 0 0 0', color: '#047857', fontSize: '13px' }}>
                                    {'\uBAA8\uB4E0 \uAE30\uB2A5\uC744 \uC790\uC720\uB86D\uAC8C \uC774\uC6A9\uD558\uC2E4 \uC218 \uC788\uC2B5\uB2C8\uB2E4.'}
                                </p>
                            </div>
                        </div>
                        {/* 직원 계정은 대표 구독 해지 불가(rules 거부) — 버튼 숨김 */}
                        {isStaff ? (
                            <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{'구독 해지는 업체 대표 계정에서 할 수 있습니다.'}</p>
                        ) : (
                            <button
                                onClick={handleCancelSubscription}
                                disabled={processing}
                                style={styles.cancelBtn}
                            >
                                {processing ? '처리 중...' : '구독 해지'}
                            </button>
                        )}
                    </div>
                ) : isClinic ? (
                    // 2026-10-30 대표 결정: 치과는 무료 — 결제 동의·카드 등록 UI 를 보이지 않는다
                    <div style={{ padding: '16px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12, fontSize: 14, color: '#065f46', fontWeight: 700, lineHeight: 1.7, textAlign: 'center' }}>
                        {'치과는 무료입니다. 결제나 카드 등록이 필요 없습니다.'}
                    </div>
                ) : (allFreeOn || noChargeReason === 'all_free') ? (
                    // 대표 10-15 안내형: 구독료·결제 조건을 미리 보여 주고, 결제 버튼 자리에는 1줄만(전면 무료 동안 서버가 결제를 받지 않음). 문구 법적보안 사후 확인
                    <div style={styles.subscribeSection}>
                        <div style={styles.subscribePriceDisplay}>
                            <span style={styles.subscribePriceAmount}>{formatNumber(displayAmount)}</span>   {/* 2026-10-30 플랜제: 고정 33,000원 → 현재 플랜 금액 */}
                            <span style={styles.subscribePriceUnit}>{'원/월'}</span>
                        </div>
                        <p style={styles.subscribeNote}>{'VAT 포함 | 세금계산서 발행 | 언제든 해지 가능'}</p>
                        <div style={{ padding: '14px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, fontSize: 13, color: '#475569', lineHeight: 1.7, textAlign: 'center' }}>
                            {noChargeReason && noChargeReason !== 'all_free' ? '지금은 결제나 카드 등록이 필요 없습니다.' : '결제는 곧 열립니다. 그 전까지는 결제나 카드 등록 없이 이용하실 수 있습니다.'}
                        </div>
                    </div>
                ) : noCharge ? (
                    // 청구 대상 아님(면제·기존 치과·관리자·해외·대상 아닌 유형) — 결제 동의·카드 등록 화면을 보이지 않음 (안내는 상단)
                    <div style={{ padding: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, fontSize: 13, color: '#475569', lineHeight: 1.7, textAlign: 'center' }}>
                        {'지금은 결제나 카드 등록이 필요 없습니다.'}
                    </div>
                ) : (
                    <div style={styles.subscribeSection}>
                        <div style={styles.subscribePriceDisplay}>
                            <span style={styles.subscribePriceAmount}>
                                {formatNumber(displayAmount)}
                            </span>
                            <span style={styles.subscribePriceUnit}>{'\uC6D0/\uC6D4'}</span>
                        </div>
                        <p style={styles.subscribeNote}>
                            {'VAT \uD3EC\uD568 | \uC138\uAE08\uACC4\uC0B0\uC11C \uBC1C\uD589 | \uC5B8\uC81C\uB4E0 \uD574\uC9C0 \uAC00\uB2A5'}
                        </p>

                        {/* 결제 유예 안내 (7일) — 자동결제가 안 된 active 구독 */}
                        {graceInfo.inGrace && (
                            <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, marginBottom: 12, fontSize: 13, color: '#991b1b', lineHeight: 1.6 }}>
                                <b>{graceFailed ? '결제가 실패했습니다.' : '이번 달 결제가 아직 완료되지 않았습니다.'}</b>
                                {` ${formatDate(graceInfo.graceUntil)}까지 카드를 확인해 주세요. 그때까지는 지금처럼 모든 기능을 이용할 수 있습니다.`}
                                <br />{'카드를 바꾸시려면 아래에서 변경한 뒤 결제해 주세요.'}
                                {!isStaff && (
                                    <div style={{ marginTop: 8 }}>
                                        <button type="button" onClick={handleCancelSubscription} disabled={processing}
                                            style={{ background: 'none', border: 'none', padding: 0, color: '#64748b', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>
                                            {'구독 해지'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 무료 체험 만료 배너 (카드 미등록) */}
                        {trialExpired && (
                            <div style={{ padding: '12px 16px', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10, marginBottom: 12, fontSize: 13, color: '#9a3412', lineHeight: 1.6 }}>
                                {'⏰ '}<b>{`${freeLabel}이 끝났습니다`}</b>{' — 아래에서 카드를 등록하면 이어서 모든 기능을 사용할 수 있습니다.'}
                            </div>
                        )}

                        {/* 무료 체험 진행 중 배너 */}
                        {trialActive && (
                            <div style={{ padding: '12px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, marginBottom: 12, fontSize: 13, color: '#065f46', lineHeight: 1.6 }}>
                                {'🎁 '}<b>{`${freeLabel} 중`}</b>{` — ${formatDate(subscription.expiresAt)}까지 모든 기능을 무료로 이용할 수 있습니다.`}
                                {!billingKey && ` ${freeLabel}이 끝난 뒤에도 계속 쓰려면 아래에서 카드를 등록해두세요 (${freeLabel} 중에는 결제되지 않습니다).`}
                            </div>
                        )}

                        {/* 결제 화면 필수 문구 + 정기결제 동의 (법무검토 ④, 약관 v6.2 제4조의2 제8항) */}
                        <div style={{ padding: '14px 16px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10, marginBottom: 12, fontSize: 13, color: '#334155', lineHeight: 1.75 }}>
                            {quoteError ? (
                                <div style={{ color: '#b91c1c' }}>{quoteError}</div>
                            ) : !quote ? (
                                <div style={{ color: '#64748b' }}>{'결제 정보를 불러오는 중...'}</div>
                            ) : (
                                <>
                                    <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 14, marginBottom: 6 }}>
                                        {`월 ${formatNumber(displayAmount)}원(부가세 포함) · ${chargeDayText} 자동결제 · 약정 없음`}
                                    </div>
                                    {trialEndsText
                                        ? <div>{isLegacyLabFree
                                            ? `기존 회원 무료 기간(${trialEndsText}까지) · 첫 결제일: ${firstChargeText}`
                                            : `무료 체험: ${trialEndsText}까지 · 첫 결제일: ${firstChargeText}`}</div>
                                        : <div>{`첫 결제일: ${firstChargeText}`}</div>}
                                    {trialEndsText && <div>{`카드를 등록해도 ${freeLabel} 중에는 결제되지 않습니다.`}</div>}
                                    <div>{'해지: 설정 › 구독 관리 › 구독 해지에서 언제든지 할 수 있습니다. 해지하면 다음 결제일부터 결제되지 않습니다.'}</div>
                                    <div>
                                        {'환불: 결제 후 7일 이내이고 이용하지 않았다면 전액 환불됩니다. 그 밖에는 이용약관 제4조의3에 따릅니다. '}
                                        <button type="button" onClick={() => setShowTerms(true)} style={{ background: 'none', border: 'none', padding: 0, color: '#4f46e5', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>{'[약관 보기]'}</button>
                                    </div>
                                    {isClinic && <div>{'의뢰서 작성·보낸 의뢰서 확인은 구독하지 않아도 무료입니다.'}</div>}
                                    {pgProvider !== 'toss' && <div style={{ color: '#64748b', fontSize: 12 }}>{'카드 등록 단계에서는 결제창에 금액이 0원으로 표시됩니다(KIS 정기결제 정책).'}</div>}
                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, cursor: 'pointer', fontWeight: 700, color: '#0f172a' }}>
                                        <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} style={{ marginTop: 3 }} />
                                        <span>{'위 금액과 자동결제 조건을 확인했으며 정기결제에 동의합니다. '}<span style={{ color: '#dc2626' }}>{'(필수)'}</span></span>
                                    </label>
                                </>
                            )}
                        </div>

                        {/* 판매자 신원정보 한 줄 — 전자상거래법 §13①(계약 전 신원정보 제공), 법무검토_가입승인결제흐름 §6-5 3번 (W8).
                            값은 SiteFooter 상수 한 곳. 통신판매업 신고번호가 비어 있으면 그 항목만 빼고, 번호를 기입하면 자동으로 표시된다
                            ("신고 준비 중" 같은 임시 문구 금지 — 법무 §6-2) */}
                        <p style={{ margin: '0 0 12px', fontSize: 11.5, color: '#94a3b8', lineHeight: 1.7, textAlign: 'center' }}>
                            {`상호 ${COMPANY_NAME} · 대표 ${CEO_NAME} · 주소 ${BIZ_ADDRESS} · 사업자등록번호 ${BIZ_NUMBER}`}
                            {ECOM_REPORT_NO ? ` · 통신판매업신고 ${ECOM_REPORT_NO}` : ''}
                            {` · 연락처 ${CONTACT_PHONE}`}
                        </p>

                        {/* 2단계: 카드 등록 → 결제 */}
                        {billingKey ? (
                            <div>
                                <div style={{ padding: '12px 16px', backgroundColor: '#ecfdf5', borderRadius: 10, border: '1px solid #a7f3d0', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 16 }}>💳</span>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>{billingKey.cardInfo?.fnNm || '카드'} 등록됨</div>
                                            <div style={{ fontSize: 11, color: '#047857' }}>****-****-****-{billingKey.cardInfo?.cardNo?.slice(-4) || '****'}</div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        {cardRegOpen && <button onClick={handleRegisterCard} style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>변경</button>}
                                        <button onClick={handleDeleteCard} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>삭제</button>
                                    </div>
                                </div>
                                <button
                                    onClick={handleSubscribe}
                                    disabled={processing || payLocked || !consentChecked || !quote}
                                    style={{ ...styles.subscribeBtn, width: '100%', opacity: (processing || payLocked || !consentChecked || !quote) ? 0.5 : 1, cursor: (processing || payLocked || !consentChecked || !quote) ? 'not-allowed' : 'pointer' }}
                                >
                                    {processing ? '진행 중...' : payLocked ? '결제 확인 중...' : (trialActive ? `자동결제 연결 (${isLegacyLabFree ? '무료 기간' : '체험'} 종료 후 월 ${formatNumber(displayAmount)}원)` : `${formatNumber(displayAmount)}원 결제하고 구독 시작`)}
                                </button>
                            </div>
                        ) : !cardRegOpen ? (
                            <div style={{ padding: '14px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 14, color: '#334155', textAlign: 'center', fontWeight: 700 }}>
                                {cardRegClosedText}
                            </div>
                        ) : (
                            <div>
                                <button
                                    onClick={handleRegisterCard}
                                    disabled={processing || payLocked || !consentChecked || !quote}
                                    style={{ ...styles.subscribeBtn, width: '100%', opacity: (processing || payLocked || !consentChecked || !quote) ? 0.5 : 1, cursor: (processing || payLocked || !consentChecked || !quote) ? 'not-allowed' : 'pointer' }}
                                >
                                    {processing ? '진행 중...' : payLocked ? '결제 확인 중...' : (trialEndsText ? `💳 카드 등록 (${isLegacyLabFree ? '무료 기간' : '체험'} 종료 후 자동결제)` : '💳 카드 등록하고 구독 시작')}
                                </button>
                                <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 8 }}>
                                    {`월 ${formatNumber(displayAmount)}원(부가세 포함) ${chargeDayText} 자동결제 · 언제든 해지 가능`}
                                </p>
                            </div>
                        )}

                        {subscription?.status === 'past_due' && (
                            <p style={styles.resubscribeNote}>
                                {'자동결제가 여러 번 실패해 구독이 멈췄습니다. 카드를 확인한 뒤 다시 결제하시면 바로 이어서 이용할 수 있습니다.'}
                            </p>
                        )}
                        {subscription?.status === 'cancelled' && (
                            <p style={styles.resubscribeNote}>
                                {endedByRefund
                                    ? '결제가 취소(환불)되어 구독이 종료되었습니다. 다시 이용하시려면 위에서 구독을 시작해 주세요.'
                                    : (quoteDate(subscription.expiresAt)?.getTime() || 0) > Date.now()
                                    ? `구독이 해지되었습니다. ${formatDate(quoteDate(subscription.expiresAt))}까지 이용할 수 있으며, 이후에는 결제되지 않습니다.`
                                    : '이전 구독이 만료되었습니다. 다시 구독하시면 모든 기능을 이용하실 수 있습니다.'}
                            </p>
                        )}

                    </div>
                )}
            </div>

            {/* \uACB0\uC81C \uB0B4\uC5ED */}
            <div style={styles.section}>
                <button
                    onClick={() => setShowHistory(!showHistory)}
                    style={styles.historyToggle}
                >
                    <span style={styles.historyToggleText}>
                        <span style={{ fontSize: '18px' }}>{'\uD83D\uDCCB'}</span>
                        {'\uACB0\uC81C \uB0B4\uC5ED'}
                    </span>
                    <span style={{
                        transform: showHistory ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                        display: 'inline-block',
                        fontSize: '16px'
                    }}>
                        {'\u25BC'}
                    </span>
                </button>

                {showHistory && (
                    <div style={styles.historyContent}>
                        {paymentHistory.length === 0 ? (
                            <div style={styles.emptyHistory}>
                                <span style={{ fontSize: '40px', opacity: 0.3 }}>{'\uD83D\uDCB3'}</span>
                                <p style={{ color: '#94a3b8', fontSize: '14px' }}>{'\uACB0\uC81C \uB0B4\uC5ED\uC774 \uC5C6\uC2B5\uB2C8\uB2E4'}</p>
                            </div>
                        ) : (
                            <div style={styles.historyList}>
                                {paymentHistory.map((item, idx) => (
                                    <div key={item.id || idx} style={styles.historyItem}>
                                        <div style={styles.historyItemLeft}>
                                            <div style={styles.historyItemName}>
                                                {item.serviceName || (item.type === 'auto_renewal' ? '월 구독료 (자동결제)' : '앱 이용료')}
                                            </div>
                                            <div style={styles.historyItemDate}>
                                                {formatDate(item.createdAt)}
                                            </div>
                                        </div>
                                        <div style={styles.historyItemRight}>
                                            <div style={styles.historyItemAmount}>
                                                {formatNumber(item.amount || pricing.totalAmount)}{'\uC6D0'}
                                            </div>
                                            <div style={{
                                                ...styles.historyItemStatus,
                                                color: item.status === 'cancelled' ? '#94a3b8' :
                                                    (item.status === 'approved' || item.source === 'kispg') ? '#059669' :
                                                    item.status === 'pending' ? '#d97706' : '#dc2626'
                                            }}>
                                                {item.status === 'cancelled' ? '\uCDE8\uC18C\uB428' :
                                                    (item.status === 'approved' || item.source === 'kispg') ? '\uC2B9\uC778' :
                                                    item.status === 'pending' ? '\uB300\uAE30' : '\uBC18\uB824'}
                                            </div>
                                            {canCancelPayment(item) && hasSelfRefund && (
                                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, maxWidth: 200, textAlign: 'right' }}>
                                                    {'화면에서 하는 직접 환불은 1회까지입니다. 환불·청약철회는 고객센터(010-2972-3733)에서 계속 받습니다.'}
                                                </div>
                                            )}
                                            {canCancelPayment(item) && !hasSelfRefund && (
                                                <button
                                                    onClick={() => handleCancelPayment(item)}
                                                    disabled={processing}
                                                    style={{ fontSize: 11, color: '#dc2626', background: 'none', border: '1px solid #fecaca', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', marginTop: 4 }}
                                                >
                                                    {'결제 취소'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 결제 동의 문구의 [약관 보기] */}
            {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}

            {/* PC \uCE74\uB4DC \uB4F1\uB85D iframe \uBAA8\uB2EC */}
            {showCardModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 14, width: 'min(480px, 94vw)', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{'\uD83D\uDCB3 \uACB0\uC81C \uCE74\uB4DC \uB4F1\uB85D'}</div>
                            <button onClick={() => { setShowCardModal(false); setProcessing(false); }} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' }}>{'\u2715'}</button>
                        </div>
                        <iframe name="kispgBillingFrame" title={'KISPG \uCE74\uB4DC \uB4F1\uB85D'} style={{ width: '100%', height: 'min(620px, 78vh)', border: 'none', display: 'block' }} />
                        <div style={{ padding: '8px 16px', fontSize: 11, color: '#94a3b8', borderTop: '1px solid #f1f5f9' }}>
                            {'\uCE74\uB4DC \uB4F1\uB85D \uB2E8\uACC4\uB77C \uACB0\uC81C\uCC3D\uC5D0\uB294 0\uC6D0\uC73C\uB85C \uD45C\uC2DC\uB429\uB2C8\uB2E4 \u2014 \uCCB4\uD5D8 \uC911\uC5D0\uB294 \uACB0\uC81C\uB418\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.'}
                        </div>
                    </div>
                </div>
            )}

            {/* \uC548\uB0B4\uC0AC\uD56D */}
            <div style={styles.infoBox}>
                <span style={{ fontSize: '20px' }}>{'\u2139\uFE0F'}</span>
                <div>
                    <strong style={{ color: '#1e40af' }}>{'\uC774\uC6A9 \uC548\uB0B4'}</strong>
                    <p style={styles.infoText}>
                        {'• 구독료는 첫 결제일부터 매월 같은 날 자동 결제됩니다(해당 날짜가 없는 달은 말일)'}<br />
                        {'\u2022 \uAD6C\uB3C5 \uD574\uC9C0 \uC2DC \uB0A8\uC740 \uAE30\uAC04 \uB3D9\uC548 \uC11C\uBE44\uC2A4 \uC774\uC6A9\uC774 \uAC00\uB2A5\uD569\uB2C8\uB2E4'}<br />
                        {'\u2022 \uC138\uAE08\uACC4\uC0B0\uC11C\uB294 \uACB0\uC81C \uC644\uB8CC \uD6C4 \uC790\uB3D9 \uBC1C\uD589\uB429\uB2C8\uB2E4'}<br />
                        {'\u2022 \uBB38\uC758\uC0AC\uD56D\uC740 \uACE0\uAC1D\uC13C\uD130\uB85C \uC5F0\uB77D\uD574\uC8FC\uC138\uC694'}
                    </p>
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: { minHeight: '100vh', backgroundColor: '#f8fafc', padding: '24px', maxWidth: '900px', margin: '0 auto' },
    loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' },
    spinner: { width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366F1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    header: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' },
    headerIcon: { width: '64px', height: '64px', backgroundColor: '#eef2ff', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#0f172a' },
    subtitle: { margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' },
    message: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', borderRadius: '12px', marginBottom: '24px', fontSize: '14px', fontWeight: '600' },
    messageSuccess: { backgroundColor: '#d1fae5', color: '#065f46' },
    messageError: { backgroundColor: '#fee2e2', color: '#991b1b' },
    statusCard: { marginBottom: '24px', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15)' },
    statusCardGradient: { background: 'linear-gradient(135deg, #6366F1 0%, #8b5cf6 50%, #a78bfa 100%)', padding: '28px', color: '#fff' },
    statusHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
    statusTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#fff' },
    statusDetails: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' },
    statusItem: { backgroundColor: 'rgba(255, 255, 255, 0.15)', borderRadius: '12px', padding: '14px', backdropFilter: 'blur(10px)' },
    statusLabel: { display: 'block', fontSize: '12px', color: 'rgba(255, 255, 255, 0.75)', marginBottom: '6px' },
    statusValue: { display: 'block', fontSize: '15px', fontWeight: '700', color: '#fff' },
    section: { backgroundColor: '#fff', borderRadius: '16px', padding: '24px', marginBottom: '24px', border: '1px solid #e2e8f0' },
    sectionTitle: { display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' },
    sectionDesc: { margin: '0 0 20px 0', fontSize: '14px', color: '#64748b' },
    featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' },
    featureCard: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px', textAlign: 'center', transition: 'all 0.2s' },
    featureIconWrap: { width: '56px', height: '56px', backgroundColor: '#eef2ff', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' },
    featureTitle: { margin: '0 0 6px 0', fontSize: '14px', fontWeight: '700', color: '#0f172a' },
    featureDesc: { margin: 0, fontSize: '12px', color: '#64748b', lineHeight: '1.4' },
    pricingCard: { backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: '14px', padding: '24px', marginBottom: '20px' },
    pricingRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #e2e8f0' },
    pricingLabel: { fontSize: '15px', color: '#475569' },
    pricingValue: { fontSize: '15px', fontWeight: '600', color: '#0f172a' },
    pricingDivider: { height: '2px', backgroundColor: '#6366F1', margin: '4px 0', opacity: 0.3 },
    pricingRowTotal: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0 4px 0' },
    pricingLabelTotal: { fontSize: '17px', fontWeight: '700', color: '#0f172a' },
    pricingValueTotal: { fontSize: '24px', fontWeight: '800', color: '#6366F1' },
    pricingPeriod: { fontSize: '14px', fontWeight: '500', color: '#64748b', marginLeft: '2px' },
    billingInfoGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
    billingInfoItem: { display: 'flex', alignItems: 'flex-start', gap: '10px', backgroundColor: '#fefce8', border: '1px solid #fef08a', borderRadius: '10px', padding: '14px' },
    billingInfoIcon: { fontSize: '20px', flexShrink: 0 },
    billingInfoLabel: { fontSize: '11px', color: '#92400e', fontWeight: '600', marginBottom: '2px' },
    billingInfoValue: { fontSize: '13px', color: '#78350f', fontWeight: '700' },
    subscribedActions: { textAlign: 'center' },
    subscribedBanner: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', backgroundColor: '#ecfdf5', border: '2px solid #6ee7b7', borderRadius: '12px', padding: '20px', marginBottom: '16px' },
    cancelBtn: { padding: '12px 32px', backgroundColor: '#fff', color: '#dc2626', border: '2px solid #fecaca', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
    subscribeSection: { textAlign: 'center', padding: '16px 0' },
    subscribePriceDisplay: { marginBottom: '8px' },
    subscribePriceAmount: { fontSize: '40px', fontWeight: '800', color: '#6366F1' },
    subscribePriceUnit: { fontSize: '16px', fontWeight: '500', color: '#64748b', marginLeft: '4px' },
    subscribeNote: { fontSize: '13px', color: '#94a3b8', margin: '0 0 24px 0' },
    subscribeBtn: { padding: '16px 48px', backgroundColor: '#6366F1', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)', transition: 'all 0.2s' },
    resubscribeNote: { fontSize: '13px', color: '#dc2626', marginTop: '12px' },
    historyToggle: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px', fontWeight: '700', color: '#0f172a' },
    historyToggleText: { display: 'flex', alignItems: 'center', gap: '8px' },
    historyContent: { marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' },
    emptyHistory: { textAlign: 'center', padding: '40px 0' },
    historyList: { display: 'flex', flexDirection: 'column', gap: '8px' },
    historyItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' },
    historyItemLeft: { flex: 1 },
    historyItemName: { fontSize: '14px', fontWeight: '600', color: '#0f172a', marginBottom: '4px' },
    historyItemDate: { fontSize: '12px', color: '#94a3b8' },
    historyItemRight: { textAlign: 'right' },
    historyItemAmount: { fontSize: '14px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' },
    historyItemStatus: { fontSize: '12px', fontWeight: '600' },
    infoBox: { display: 'flex', gap: '16px', padding: '20px', backgroundColor: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe' },
    infoText: { margin: '8px 0 0 0', fontSize: '13px', color: '#1e40af', lineHeight: '1.8' }
};

export default AppSubscription;
